import React from 'react'
import Box from 'components/Box'
import {
  Container,
  CheckboxInput,
  HiddenInput,
  Caption,
  CheckmarkIcon,
  CheckboxLabel,
  CheckboxLabelText,
  LoadingWrapper,
  SuccessWrapper,
} from './Checkbox.styles'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { ReactComponent as Checkmark } from 'icons/checkmark-16.svg'

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  children?: React.ReactNode
  label?: string
  className?: string
  disabled?: boolean
  checked?: boolean
  help?: string
  loading?: boolean
  success?: boolean
}

/**
 * Checkbox primitive is a lightweight wrapper around a native checkbox with a
 *  custom style and some extra capabilities like help message.
 *
 * It should be used as a controlled component using `checked` and `onChange`.
 *
 */
const Checkbox = ({
  checked,
  children,
  disabled,
  className,
  label,
  help,
  loading,
  success,
  ...props
}: CheckboxProps) => {
  children = children ? children : label
  const as = children ? 'label' : 'div'

  return (
    <Container className={className} as={as}>
      <Box opacity={disabled ? 0.5 : undefined}>
        <CheckboxInput checked={checked}>
          <HiddenInput
            {...props}
            disabled={disabled || loading}
            type="checkbox"
            aria-label={label}
            checked={checked}
          />
          {checked && <CheckmarkIcon />}
        </CheckboxInput>
      </Box>
      <CheckboxLabel disabled={disabled ?? false}>
        {children && <CheckboxLabelText>{children}</CheckboxLabelText>}
        {loading && (
          <LoadingWrapper>
            <LoadingPlaceholder size="16px" />
          </LoadingWrapper>
        )}
        {success && (
          <SuccessWrapper>
            <Checkmark width={16} />
          </SuccessWrapper>
        )}
        {help && <Caption>{help}</Caption>}
      </CheckboxLabel>
    </Container>
  )
}

export default Checkbox
